import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Button, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import AuthorizedBox from "@entrilia/shared/components/AuthorizedBox.tsx";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle.ts";
import useRequest from "@entrilia/shared/hooks/useRequest.ts";
import biApi from "../../../../../../api/biApi.ts";
import useClientContext from "../../../../../../context/useClientContext.ts";
import { useReportsContext } from "../../contexts/reports/ReportsContext.ts";
import AddGroupDialog from "../../groups/actions/AddGroupDialog.tsx";
import { useGroupsContext } from "../../groups/contexts/GroupsContext.ts";
import ReportsPageContainer from "../components/ReportsPageContainer.tsx";
import ReportsSideMenu from "../components/ReportsSideMenu.tsx";
import ReportsGridWrapper from "../grid/ReportsGridWrapper.tsx";
import useReportsWithSideMenu from "../hooks/useReportsWithSideMenu.ts";
import { getEditSystemReportUrl } from "../utilities/editReportUrl.ts";

const CompanySystemReportsContainer = () => {
  usePageTitle("System Reports");
  const navigate = useNavigate();
  const { clientCode } = useClientContext();
  const { groups: reportGroups, actions: groupActions } = useGroupsContext();
  const { reports } = useReportsContext();

  const {
    filteredReports,
    groups,
    reportCountsByGroup,
    sideMenuFilter,
    sideMenuFilterType,
    selectedFilterLabel,
    setSideMenuFilter,
    toggleFavorite,
  } = useReportsWithSideMenu({
    reports,
    reportGroups,
    clientCode,
    getFavoriteReports: biApi.systemReports.getFavoriteReports,
    toggleFavoriteReport: biApi.systemReports.toggleFavoriteReport,
  });

  const [showAddGroupDialog, setShowAddGroupDialog] = useState(false);

  const { request: createDraftReport, isLoading: isCreatingDraftReport } = useRequest(
    biApi.systemReports.createDraftReport
  );

  const onCreateDraftReport = useCallback(async () => {
    const [report] = await createDraftReport();
    if (report) {
      navigate(getEditSystemReportUrl(clientCode, report.reportId));
    }
  }, [createDraftReport, clientCode, navigate]);

  return (
    <Stack direction="row" sx={{ height: "100%", width: "100%" }}>
      <ReportsSideMenu
        title="System Reports"
        selectedFilter={sideMenuFilter}
        onFilterChange={setSideMenuFilter}
        groups={groups}
        reportCountsByGroup={reportCountsByGroup}
        groupType="report"
        onAddGroup={() => setShowAddGroupDialog(true)}
        onReorderGroups={(groups) => groupActions.reorder(clientCode, groups)}
      />
      <ReportsPageContainer
        title={selectedFilterLabel}
        Actions={
          <>
            <AuthorizedBox permissions={["ManageReports"]}>
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={onCreateDraftReport}
                disabled={isCreatingDraftReport}
                loading={isCreatingDraftReport}
              >
                Create New
              </Button>
            </AuthorizedBox>
          </>
        }
      >
        <ReportsGridWrapper
          reports={filteredReports}
          isGroupSelected={sideMenuFilterType === "group"}
          onToggleFavorite={toggleFavorite}
        />
      </ReportsPageContainer>
      {showAddGroupDialog && <AddGroupDialog clientCode={clientCode} onClose={() => setShowAddGroupDialog(false)} />}
    </Stack>
  );
};

export default CompanySystemReportsContainer;
